import { useState, useCallback, useMemo, useRef } from 'react';
import { FileInfo } from '../FileUpload/types';
import { DanelFileInfo } from '../DanelFileUploadItem/types';
import { BankType, VaultParseResult } from '../../types/generatedTypes';
import Actions from '../../store/Actions';
import { useSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { ModalState } from './types';
import APIs from '../../application/APIs';

export const useDanelFileUploadModal = (
  onUpload: (
    files: DanelFileInfo[],
    returnExcel?: boolean,
    alreadySaved?: boolean
  ) => void,
  onClose: () => void
) => {
  const [files, setFiles] = useState<DanelFileInfo[]>([]);
  const [returnExcel, setReturnExcel] = useState<boolean>(false);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalState>('uploading');
  const [parseResults, setParseResults] = useState<VaultParseResult[]>([]);
  const isSavingRef = useRef<boolean>(false);
  const { showSnackbar } = useSnackbar();

  const detectFiles = useCallback(
    async (filesToDetect: DanelFileInfo[]) => {
      setIsDetecting(true);
      try {
        // Extract File objects from DanelFileInfo
        const fileObjects = filesToDetect.map(f => f.file);
        const detectionResponse = await Actions.detectVaultTypes(fileObjects);

        // Map detection results back to files
        const updatedFiles = filesToDetect.map(danelFile => {
          const result = detectionResponse.results.find(
            r => r.filename === danelFile.name
          );

          if (!result) {
            return danelFile;
          }

          const detectedTypes = result.detected_types as BankType[];

          if (detectedTypes.length === 0) {
            // No detection
            return {
              ...danelFile,
              isDetected: false,
              detectedBanks: [],
              isAmbiguous: false,
              bank: undefined,
            };
          } else if (detectedTypes.length === 1) {
            // Single detection - set bank automatically
            return {
              ...danelFile,
              isDetected: true,
              detectedBanks: detectedTypes,
              isAmbiguous: false,
              bank: detectedTypes[0],
            };
          } else {
            // Multiple detections - ambiguous
            return {
              ...danelFile,
              isDetected: true,
              detectedBanks: detectedTypes,
              isAmbiguous: true,
              bank: undefined,
            };
          }
        });

        return updatedFiles;
      } catch (error) {
        showSnackbar({
          title: 'Detection Failed',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to detect vault types',
          type: SnackbarType.error,
        });
        return filesToDetect;
      } finally {
        setIsDetecting(false);
      }
    },
    [showSnackbar]
  );

  const handleFilesSelect = useCallback(
    async (selectedFiles: FileInfo[]) => {
      const danelFiles = selectedFiles.map(file => ({
        ...file,
        bank: undefined,
      }));

      // Detect vault types for selected files
      const detectedFiles = await detectFiles(danelFiles);
      setFiles(detectedFiles);
    },
    [detectFiles]
  );

  const handleBankChange = useCallback((index: number, bank: BankType) => {
    setFiles(prevFiles =>
      prevFiles.map((file, i) =>
        i === index
          ? {
              ...file,
              bank,
              isDetected: true,
              isAmbiguous: false,
            }
          : file
      )
    );
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  }, []);

  const canUpload = useMemo(() => {
    return files.length > 0 && files.every(file => file.bank !== undefined);
  }, [files]);

  const handleReturnExcelChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setReturnExcel(event.target.checked);
    },
    []
  );

  /**
   * Shared internal helper function for saving vault files to backend.
   * Handles the complete save flow including API calls, state updates, and cleanup.
   *
   * @param filesToSave - Array of DanelFileInfo objects to save
   * @param showPartialSuccessMessage - When true, shows "Partial Success" with failed file names; when false, shows simple "Success" message
   * @returns Promise that resolves to true on success, false on failure
   */
  const saveFilesInternal = useCallback(
    async (
      filesToSave: DanelFileInfo[],
      showPartialSuccessMessage: boolean
    ): Promise<boolean> => {
      // Prevent double-calling
      if (isSavingRef.current) {
        return false;
      }

      isSavingRef.current = true;

      try {
        const successfulFiles = filesToSave.filter(
          f => f.parseStatus === 'success' && f.parsedData
        );

        if (successfulFiles.length === 0) {
          // All files failed or produced zero rows - still need to clear old data from database
          // Call processVaultFiles which will trigger _aggregate_and_save to clear old DanelFiles
          const filesWithBanks = filesToSave.filter(f => f.bank !== undefined);
          if (filesWithBanks.length > 0) {
            try {
              const filesToProcess = filesWithBanks.map(f => ({
                file: f.file,
                bank: f.bank!,
                name: f.name,
              }));
              // This call will clear old data even though it produces zero rows
              await APIs.processVaultFiles(filesToProcess, false);
            } catch {
              // If clearing fails, log but continue - we still want to refresh UI
            }
          }

          if (showPartialSuccessMessage) {
            showSnackbar({
              title: 'No Valid Files',
              message:
                'No successfully parsed files to save - previous data has been cleared',
              type: SnackbarType.warning,
            });
          }
          // Call onUpload with alreadySaved=true so parent refreshes the table
          // This ensures the table reflects current state (now empty) after clearing
          onUpload(filesToSave, returnExcel, true);

          // Reset state and close modal
          setFiles([]);
          setReturnExcel(false);
          setModalState('uploading');
          setParseResults([]);
          onClose();

          return false;
        }

        if (returnExcel) {
          // Use processVaultFiles for downloads
          // This endpoint receives raw File objects and processes them fresh,
          // avoiding base64 encoding/decoding issues that could corrupt the Excel files.
          // The working /api/vault/process_vault_files endpoint generates reliable ZIP downloads.
          const filesToProcess = successfulFiles.map(f => ({
            file: f.file,
            bank: f.bank!,
            name: f.name,
          }));
          await APIs.processVaultFiles(filesToProcess, true);
        } else {
          // Use saveParsedVaults for database-only saves (no download)
          // This is more efficient as it reuses already-parsed data
          // IMPORTANT: Must send complete parse result format matching what parse endpoint returns
          // Backend expects: { success: bool, filename: str, bank_type: str, row_count: int, parsed_data: {...}, error: null }
          const parsedDataToSave = successfulFiles.map(f => ({
            success: true,
            filename: f.name,
            bank_type: f.bank!,
            row_count: f.rowCount || 0,
            parsed_data: f.parsedData,
            error: null,
          }));
          await APIs.saveParsedVaults(parsedDataToSave, false);
        }

        // Show appropriate snackbar message
        if (showPartialSuccessMessage) {
          const failedFiles = filesToSave.filter(
            f => f.parseStatus === 'error'
          );
          const failedFileNames = failedFiles.map(f => f.name).join(', ');

          showSnackbar({
            title: 'Partial Success',
            message: `Successfully uploaded ${successfulFiles.length} files. Failed: ${failedFileNames}`,
            type: SnackbarType.warning,
          });
        } else {
          showSnackbar({
            title: 'Success',
            message: `Successfully saved all ${successfulFiles.length} vault file(s)`,
            type: SnackbarType.success,
          });
        }

        // Call the original onUpload callback with alreadySaved flag to prevent duplicate processing
        onUpload(filesToSave, returnExcel, true);

        // Reset state
        setFiles([]);
        setReturnExcel(false);
        setModalState('uploading');
        setParseResults([]);

        // Close the modal
        onClose();

        return true;
      } catch (error) {
        showSnackbar({
          title: 'Save Failed',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to save vault files',
          type: SnackbarType.error,
        });
        return false;
      } finally {
        isSavingRef.current = false;
      }
    },
    [returnExcel, onUpload, showSnackbar, onClose]
  );

  const handleSaveAndClose = useCallback(
    async (filesToSave: DanelFileInfo[]) => {
      const success = await saveFilesInternal(filesToSave, false);
      if (!success) {
        // Re-throw error if save failed for backward compatibility
        throw new Error('Save failed');
      }
    },
    [saveFilesInternal]
  );

  const handleUpload = useCallback(async () => {
    if (!canUpload) return;

    // Set to 'results' state immediately
    setModalState('results');

    // Set all files to parsing state
    setFiles(prevFiles =>
      prevFiles.map(f => ({
        ...f,
        parseStatus: 'parsing' as const,
      }))
    );

    try {
      // Prepare files for parsing
      const filesToParse = files.map(f => ({
        file: f.file,
        bank: f.bank!,
        name: f.name,
      }));

      // Call parse API
      const parseResponse = await Actions.parseVaultFiles(filesToParse);

      // Store parse results
      setParseResults(parseResponse.results);

      // Update files with parse results
      const updatedFiles = files.map(file => {
        const result = parseResponse.results.find(
          r => r.filename === file.name
        );
        if (!result) return file;

        if (result.success) {
          // Use row_count from backend response (0 means all rows were filtered out as empty)
          const rowCount = result.row_count ?? 0;

          return {
            ...file,
            parseStatus: 'success' as const,
            rowCount: rowCount,
            parsedData: result.parsed_data,
          };
        } else {
          return {
            ...file,
            parseStatus: 'error' as const,
            parseError: result.error || 'Parse failed',
          };
        }
      });

      setFiles(updatedFiles);

      // Check if ALL succeeded
      const allSucceeded = updatedFiles.every(f => f.parseStatus === 'success');

      if (allSucceeded) {
        // Auto-save and close
        await handleSaveAndClose(updatedFiles);
      }
    } catch (error) {
      // Set all files to error state
      setFiles(prevFiles =>
        prevFiles.map(f => ({
          ...f,
          parseStatus: 'error' as const,
          parseError: error instanceof Error ? error.message : 'Parse failed',
        }))
      );

      showSnackbar({
        title: 'Parse Failed',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to parse vault files',
        type: SnackbarType.error,
      });
    }
  }, [files, canUpload, showSnackbar, handleSaveAndClose]);

  const handleFinish = useCallback(async () => {
    await saveFilesInternal(files, true);
  }, [saveFilesInternal, files]);

  const handleCancel = useCallback(() => {
    // Reset state and close without any side effects
    setFiles([]);
    setReturnExcel(false);
    setModalState('uploading');
    setParseResults([]);
    isSavingRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    // Reset state and close without any side effects
    setFiles([]);
    setReturnExcel(false);
    setModalState('uploading');
    setParseResults([]);
    isSavingRef.current = false;
  }, []);

  return {
    files,
    canUpload,
    returnExcel,
    isDetecting,
    modalState,
    parseResults,
    handleFilesSelect,
    handleBankChange,
    handleRemoveFile,
    handleReturnExcelChange,
    handleUpload,
    handleFinish,
    handleCancel,
    handleClose,
  };
};
